import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import NodeLoader from "./NodeLoader";
import { METHOD_NOTE } from "@/lib/advisor";

/**
 * Full-screen "Building Your Workforce Blueprint" sequence.
 * Visually advances through stages while real backend work runs. The final
 * stages hold until `done` is true, so a stage never completes before the
 * corresponding processing has finished.
 */
export const FinalAnalysis = ({ stages = [], done = false, reduced = false }) => {
  const [active, setActive] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const holdIndex = Math.max(0, stages.length - 2); // don't auto-pass the last 2 until done
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (reduced) return;
    const stepMs = 700;
    const id = setInterval(() => {
      setActive((a) => (a < holdIndex ? a + 1 : a));
    }, stepMs);
    const t = setInterval(() => setElapsed(Date.now() - startRef.current), 500);
    return () => { clearInterval(id); clearInterval(t); };
  }, [holdIndex, reduced]);

  useEffect(() => {
    if (done) setActive(stages.length); // mark all complete
  }, [done, stages.length]);

  const tooLong = elapsed > 12000 && !done;

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-4" data-testid="final-analysis">
      <NodeLoader size={54} reduced={reduced} />
      <h2 className="text-2xl sm:text-3xl font-bold mt-6 text-center">
        Building Your Workforce Blueprint
      </h2>
      <p className="text-white/55 text-sm mt-2 text-center max-w-md">
        {tooLong
          ? "This assessment includes several operational factors. I'm finishing the final recommendation now."
          : "Applying Rendeavored's staffing rules to your responses."}
      </p>

      <div className="w-full max-w-md mt-8 space-y-2.5">
        {stages.map((s, i) => {
          const isDone = i < active;
          const isActive = i === active && !isDone;
          return (
            <motion.div
              key={s}
              initial={reduced ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: reduced ? 0 : Math.min(i * 0.05, 0.3) }}
              className="flex items-center gap-3 rounded-lg border px-4 py-2.5"
              style={{
                backgroundColor: isActive ? "#101a33" : "#0F1424",
                borderColor: isActive ? "rgba(30,107,255,0.35)" : "rgba(255,255,255,0.08)",
              }}
              data-testid={`analysis-stage-${i}`}
            >
              <span className="shrink-0">
                {isDone ? (
                  <span className="h-5 w-5 rounded-full bg-[#1E6BFF] flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </span>
                ) : isActive ? (
                  <Loader2 size={18} className="text-[#66B2FF] animate-spin" />
                ) : (
                  <span className="h-5 w-5 rounded-full border border-white/20 block" />
                )}
              </span>
              <span className={`text-sm ${isDone ? "text-white/55" : isActive ? "text-white" : "text-white/40"}`}>
                {s}
              </span>
            </motion.div>
          );
        })}
      </div>

      <p className="text-white/35 text-xs mt-8 max-w-md text-center leading-relaxed">
        {METHOD_NOTE}
      </p>
    </div>
  );
};

export default FinalAnalysis;
